import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import type { AppEvent, EventBranding } from '../../store/eventsSlice'
import type { Registration } from '../../store/registrationsSlice'
import { RoleBadge } from '../../components/RoleBadge'
import { TicketPresentationSection } from '../../components/TicketPresentationSection'
import { isTicketActive, loadGoogleFont, CURATED_FONTS, mapsUrl } from '../../utils/event'
import { formatFullName } from '../../utils/participantMappers'
import { formatEventDateTime } from '../../utils/datetime'
import { ENTRANCE_QR_PREFIX } from '../../utils/ticketVerification'

// QR codes must always be black-on-white — it's the highest-contrast, most
// reliably scannable combination across cameras and lighting conditions.
const QR_FG_COLOR = '#000000'
const QR_BG_COLOR = '#ffffff'

/** Formats a ticket-page datetime in the event's own timezone (never the viewer's),
 *  using `locale` (the app's current UI language) for month/weekday names and date order.
 *  Omits the timezone suffix — DAJ feedback: ticket page shows wall-clock time only. */
function formatDateTime(iso: string, timezone: string | null | undefined, locale: string): string {
  return formatEventDateTime(
    iso,
    timezone,
    locale,
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    { includeTimeZoneName: false }
  )
}

// ── Shared icons ──────────────────────────────────────────────────────────────

/** Calendar glyph next to the event's start date/time in {@link EventHeader}. */
export const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mt-0.5 shrink-0"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
)

/** Pin glyph next to the event's address in {@link EventHeader}. */
export const MapPinIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mt-0.5 shrink-0"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

// ── Shared event header ───────────────────────────────────────────────────────

/** Props for {@link EventHeader}. */
export interface EventHeaderProps {
  title: string
  startDateTime?: string
  timezone?: string | null
  address?: string
  logo?: { dataUrl: string }
  primary: string
  accent: string
  headingFamily: string
  /** App's current UI language (e.g. `i18n.language`) for the date's month/weekday names. */
  locale: string
}

/** Logo, title, date/time and address block shared by every ticket-card state. */
export function EventHeader({
  title,
  startDateTime,
  timezone,
  address,
  logo,
  primary,
  accent,
  headingFamily,
  locale,
}: EventHeaderProps) {
  return (
    <>
      {logo && <img src={logo.dataUrl} alt={title} className="h-16 max-w-[200px] object-contain" />}

      <h1
        className="text-center text-xl leading-tight font-black tracking-tight"
        style={{ fontFamily: headingFamily }}
      >
        {title}
      </h1>

      <div className="h-px w-full opacity-15" style={{ background: primary }} />

      <div className="flex w-full flex-col gap-2.5 text-sm">
        {startDateTime && (
          <div className="flex w-full items-start gap-2 text-left">
            <span className="mt-0.5 shrink-0" style={{ color: accent }}>
              <CalendarIcon />
            </span>
            <span className="min-w-0 flex-1 leading-snug opacity-80">
              {formatDateTime(startDateTime, timezone, locale)}
            </span>
          </div>
        )}
        {address && (
          <a
            href={mapsUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-start gap-2 text-left transition-opacity hover:opacity-70"
          >
            <span className="mt-0.5 shrink-0" style={{ color: accent }}>
              <MapPinIcon />
            </span>
            <span
              className="min-w-0 flex-1 text-xs leading-snug break-words opacity-80"
              style={{ wordBreak: 'auto-phrase' }}
            >
              {address}
            </span>
          </a>
        )}
      </div>
    </>
  )
}

// ── Ticket card wrapper ───────────────────────────────────────────────────────

interface CardProps {
  b: { background: string; primary: string; accent: string }
  children: React.ReactNode
}

/** Rounded, branded card frame shared by every ticket-card state (accepted, pending, rejected...). */
export function TicketCard({ b, children }: CardProps) {
  return (
    <div
      className="w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl"
      style={{ background: b.background, border: `1px solid ${b.primary}30` }}
    >
      <div
        data-testid="ticket-card-accent-strip"
        className="h-1.5 w-full"
        style={{ background: b.accent }}
      />
      <div className="flex flex-col items-center gap-6 px-8 py-10">{children}</div>
      <div
        data-testid="ticket-card-primary-strip"
        className="h-1.5 w-full"
        style={{ background: b.primary }}
      />
    </div>
  )
}

// ── Registration details summary ────────────────────────────────────────────
// Shows back to the participant exactly what they submitted — full name, email,
// and company. Personal-data and media consents are not repeated on the ticket:
// personal data is required to register (see #242 / TC-TICKET-016) and media
// consent is collected on the registration form only.
//
// Lives here rather than in TicketPage because both the accepted ticket (rendered by
// {@link TicketView}) and TicketPage's own pending/rejected states need it.

/** Props for {@link RegistrationDetails}. */
export interface RegistrationDetailsProps {
  b: { primary: string; accent: string; text: string }
  headingFamily: string
  /** Omitted on the accepted ticket, where the name/email are already shown as a chip above. */
  fullName?: string
  email?: string
  company?: string
  /** When true, consents were collected by the organiser — hide the details box on the ticket. */
  registeredByOrganizer?: boolean
  t: (key: string) => string
}

/** Read-only summary of what the participant submitted when registering. */
export function RegistrationDetails({
  b,
  headingFamily,
  fullName,
  email,
  company,
  registeredByOrganizer = false,
  t,
}: RegistrationDetailsProps) {
  const hasIdentity = fullName || email || company
  if (!hasIdentity || registeredByOrganizer) {
    return null
  }

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-xl px-4 py-3.5 text-left"
      style={{ background: `${b.primary}0A`, border: `1px solid ${b.primary}1C` }}
    >
      <p
        className="text-[10px] font-bold tracking-widest uppercase opacity-50"
        style={{ fontFamily: headingFamily }}
      >
        {t('ticket_details_label')}
      </p>

      <div className="flex flex-col gap-1">
        {fullName && <p className="text-sm font-semibold">{fullName}</p>}
        {email && <p className="text-xs opacity-60">{email}</p>}
        {company && <p className="text-xs opacity-60">{company}</p>}
      </div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

/** Props for {@link TicketView}. */
export interface TicketViewProps {
  event: AppEvent
  registration: Registration
  /**
   * When true, renders inside the event editor's live preview pane with mock participant
   * data rather than as a real ticket: presentation upload/download becomes inert so the
   * preview stays safe and doesn't distract the organizer from editing the form.
   */
  previewMode?: boolean
  /** Opens the presentation upload modal. Ignored in `previewMode`. */
  onManagePresentationClick?: () => void
  /** Downloads the submitted presentation file. Ignored in `previewMode`. */
  onDownloadPresentation?: () => Promise<void>
  /** When false, hides the presentation download button even if a file name is present. */
  canDownloadPresentation?: boolean
  /**
   * Overrides whether the entrance QR is shown as active. Defaults to
   * `isTicketActive(event)`. {@link TicketPage}'s API data path (`?tk=`) has its own
   * "active unless an activation date is set and still in the future" rule that differs
   * from the Redux-backed default, so it computes and passes this explicitly.
   */
  ticketActive?: boolean
  /**
   * True once the event's end date has passed. The entrance QR is useless from that point on,
   * so {@link postEventDownloads} takes its place.
   */
  eventEnded?: boolean
  /** Certificate of Attendance + event media downloads, rendered instead of the entrance QR
   * when {@link eventEnded}. `null` for participants who did not attend — they get neither. */
  postEventDownloads?: React.ReactNode
  /** Rendered below the ticket card — e.g. the "Back to event" / "View event page" links.
   * Omitted entirely in the editor's live preview. */
  footer?: React.ReactNode
}

/**
 * Presentational rendering of an accepted participant's ticket. Shared by {@link TicketPage}
 * (real participant-facing route, backed by API/store data) and the event editor's live
 * preview pane (backed by in-memory, unsaved form state + a mock participant) so organizers
 * see a true WYSIWYG result of what attendees will receive.
 */
export function TicketView({
  event,
  registration,
  previewMode = false,
  onManagePresentationClick,
  onDownloadPresentation,
  canDownloadPresentation = true,
  ticketActive,
  eventEnded = false,
  postEventDownloads,
  footer,
}: TicketViewProps) {
  const { t, i18n } = useTranslation()
  const b: EventBranding = event.branding

  useEffect(() => {
    loadGoogleFont(b.headingFont)
    loadGoogleFont(b.bodyFont)
  }, [b.headingFont, b.bodyFont])

  const headingFamily = b.headingFont
    ? `'${b.headingFont}', ${CURATED_FONTS.find((f) => f.family === b.headingFont)?.family ?? 'serif'}`
    : 'serif'
  const bodyFamily = b.bodyFont ? `'${b.bodyFont}', sans-serif` : 'sans-serif'

  const cssVars = {
    '--ev-bg': b.background,
    '--ev-text': b.text,
    '--ev-primary': b.primary,
    '--ev-accent': b.accent,
    '--ev-heading-font': headingFamily,
    '--ev-body-font': bodyFamily,
  } as React.CSSProperties

  const isActive = ticketActive ?? isTicketActive(event)

  return (
    <div
      style={{
        ...cssVars,
        background: 'var(--ev-bg)',
        color: 'var(--ev-text)',
        fontFamily: 'var(--ev-body-font)',
      }}
      className="flex min-h-screen flex-col items-center justify-center px-6 py-12"
    >
      <TicketCard b={b}>
        <EventHeader
          title={event.title}
          startDateTime={event.startDateTime || undefined}
          timezone={event.timezone}
          address={event.address || undefined}
          logo={b.logo}
          primary={b.primary}
          accent={b.accent}
          headingFamily={headingFamily}
          locale={i18n.language}
        />

        {/* Participant name chip */}
        <div
          className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5"
          style={{ background: `${b.accent}14`, border: `1px solid ${b.accent}28` }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: b.accent }}
            aria-hidden="true"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span className="text-sm font-semibold" style={{ color: b.accent }}>
            {formatFullName(registration.firstName, registration.lastName)}
          </span>
        </div>

        {/* Company for organiser-registered participants — below name, above email. Required
            again after the first real event surfaced they're needed on the ticket itself (see
            #270) — reverses the 10 Aug 2026 "identity strip-down" (TC-TICKET-022). */}
        {registration.registeredByOrganizer && registration.company && (
          <p className="-mt-1 text-center text-xs opacity-60">{registration.company}</p>
        )}

        {/* Registered email */}
        {registration.email && (
          <p className="-mt-1 text-center text-xs opacity-50">{registration.email}</p>
        )}

        {/* Registration summary — company only on accepted tickets (name/email shown above) */}
        {!registration.registeredByOrganizer && registration.company && (
          <RegistrationDetails
            b={b}
            headingFamily={headingFamily}
            company={registration.company}
            t={t}
          />
        )}

        {/* Role badge — shown for every role including Audience (#270's DEV-RESOLUTION asked for
            these fields "for all participants"), using the same colored pill used elsewhere in
            the app (RoleBadge / ROLE_STYLES) rather than a bespoke ticket style. */}
        {registration.role && <RoleBadge role={registration.role} />}

        <div className="h-px w-full opacity-15" style={{ background: b.primary }} />

        {/* ── Entrance QR Code, or — once the event has ended — the post-event
            downloads (Certificate of Attendance + Event Media). Placed directly
            below the name so attendees don't miss it (DAJ ticket-page review). ── */}
        {eventEnded ? (
          postEventDownloads
        ) : (
          <div className="flex w-full flex-col items-center gap-4">
            <p
              className="text-[10px] font-bold tracking-[0.22em] uppercase opacity-40"
              style={{ fontFamily: bodyFamily }}
            >
              {t('ticket_qr_section_label')}
            </p>

            {isActive ? (
              <div className="flex flex-col items-center gap-3">
                <div
                  className="rounded-2xl bg-white p-4 shadow-lg"
                  style={{ outline: `3px solid ${b.accent}40` }}
                >
                  <QRCodeSVG
                    value={`${ENTRANCE_QR_PREFIX}${registration.secretToken}`}
                    size={192}
                    level="H"
                    fgColor={QR_FG_COLOR}
                    bgColor={QR_BG_COLOR}
                  />
                </div>
                <p className="text-xs opacity-40">{t('ticket_qr_scan_hint')}</p>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center gap-3">
                <button
                  type="button"
                  disabled
                  className="w-full cursor-not-allowed rounded-xl px-5 py-3.5 text-sm font-bold tracking-wide"
                  style={{
                    background: `${b.accent}22`,
                    color: b.accent,
                    border: `2px dashed ${b.accent}44`,
                    fontFamily: headingFamily,
                    opacity: 0.6,
                  }}
                >
                  {t('ticket_qr_btn')}
                </button>
                {event.ticketActivation && (
                  <p className="text-center text-xs opacity-45">
                    {t('ticket_qr_available_from')}&nbsp;
                    <span className="font-semibold">
                      {formatDateTime(event.ticketActivation, event.timezone, i18n.language)}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attended badge — shown after organizer confirms attendance */}
        {registration.attended && (
          <div
            className="flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2"
            style={{ background: `${b.primary}18`, border: `1px solid ${b.primary}30` }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: b.primary }}
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="text-xs font-bold" style={{ color: b.primary }}>
              {t('ticket_attended_badge')}
            </span>
          </div>
        )}

        {/* Private note from organizer */}
        {registration.privateNote && (
          <div
            className="w-full rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
            style={{ background: `${b.accent}12`, border: `1px solid ${b.accent}25` }}
          >
            <p className="mb-1 text-[10px] font-bold tracking-widest uppercase opacity-50">
              {t('ticket_private_note_label')}
            </p>
            {registration.privateNote}
          </div>
        )}

        {/* Ticket announcement */}
        {event.ticketAnnouncement && (
          <div
            className="w-full rounded-xl px-4 py-3 text-left text-sm leading-relaxed whitespace-pre-wrap opacity-80"
            style={{ background: `${b.primary}12`, border: `1px solid ${b.primary}20` }}
          >
            {event.ticketAnnouncement}
          </div>
        )}

        {/* Presentation upload/status section — Speaker/Host only */}
        <TicketPresentationSection
          registration={registration}
          b={b}
          headingFamily={headingFamily}
          bodyFamily={bodyFamily}
          onManageClick={previewMode ? () => {} : (onManagePresentationClick ?? (() => {}))}
          onDownload={previewMode ? undefined : onDownloadPresentation}
          canDownload={canDownloadPresentation}
        />
      </TicketCard>
      {footer}
    </div>
  )
}
